import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Input from './Input';

const GlobalSearch = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const mockData = {
    properties: [
      { id: 'P001', address: '123 High Street, London', type: 'Flat', bedrooms: 2 },
      { id: 'P002', address: '45 Park Avenue, Manchester', type: 'House', bedrooms: 3 },
      { id: 'P003', address: '78 Queen Road, Birmingham', type: 'Apartment', bedrooms: 1 },
    ],
    tenancies: [
      { id: 'T001', tenant: 'John Smith', property: '123 High Street', status: 'Active' },
      { id: 'T002', tenant: 'Sarah Johnson', property: '45 Park Avenue', status: 'Active' },
    ],
    contractors: [
      { id: 'C001', name: 'ABC Plumbing', trade: 'Plumbing', phone: '020 1234 5678' },
      { id: 'C002', name: 'XYZ Electrical', trade: 'Electrical', phone: '020 8765 4321' },
    ],
  };

  useEffect(() => {
    if (isOpen && inputRef?.current) {
      inputRef?.current?.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!searchQuery?.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    const timeoutId = setTimeout(() => {
      const query = searchQuery?.toLowerCase();
      const results = [];

      mockData?.properties?.forEach(property => {
        if (property?.address?.toLowerCase()?.includes(query) || property?.id?.toLowerCase()?.includes(query)) {
          results?.push({ ...property, category: 'Properties' });
        }
      });

      mockData?.tenancies?.forEach(tenancy => {
        if (tenancy?.tenant?.toLowerCase()?.includes(query) || tenancy?.id?.toLowerCase()?.includes(query)) {
          results?.push({ ...tenancy, category: 'Tenancies' });
        }
      });

      mockData?.contractors?.forEach(contractor => {
        if (contractor?.name?.toLowerCase()?.includes(query) || contractor?.trade?.toLowerCase()?.includes(query)) {
          results?.push({ ...contractor, category: 'Contractors' });
        }
      });

      setSearchResults(results);
      setIsSearching(false);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleResultClick = (result) => {
    if (result?.category === 'Properties') {
      navigate('/properties-management-screen', { state: { selectedProperty: result?.id } });
    } else if (result?.category === 'Tenancies') {
      navigate('/properties-management-screen', { state: { selectedTenancy: result?.id } });
    } else if (result?.category === 'Contractors') {
      navigate('/maintenance-kanban-workflow-board', { state: { selectedContractor: result?.id } });
    }
    onClose();
    setSearchQuery('');
  };

  if (!isOpen) return null;

  const groupedResults = searchResults?.reduce((acc, result) => {
    if (!acc?.[result?.category]) {
      acc[result.category] = [];
    }
    acc?.[result?.category]?.push(result);
    return acc;
  }, {});

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-3xl" onClick={(e) => e?.stopPropagation()}>
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1">
            <Input
              ref={inputRef}
              type="search"
              placeholder="Search properties, tenants, contractors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
            />
          </div>
          <button
            onClick={onClose}
            className="h-12 w-12 rounded-lg bg-input border border-border flex items-center justify-center transition-smooth hover:bg-muted"
            aria-label="Close"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto">
          {isSearching && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin">
                <Icon name="Loader2" size={24} color="var(--color-accent)" />
              </div>
            </div>
          )}

          {!isSearching && searchQuery && searchResults?.length === 0 && (
            <div className="text-center py-8">
              <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
              <p className="text-muted-foreground">No results found for "{searchQuery}"</p>
            </div>
          )}

          {!isSearching && Object.keys(groupedResults)?.length > 0 && (
            <div className="space-y-4">
              {Object.entries(groupedResults)?.map(([category, results]) => (
                <div key={category}>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2 font-caption">{category}</h3>
                  <div className="space-y-1">
                    {results?.map((result, index) => (
                      <button
                        key={index}
                        onClick={() => handleResultClick(result)}
                        className="w-full text-left p-3 rounded-lg bg-input border border-border transition-smooth hover:bg-muted hover:border-accent"
                      >
                        {result?.category === 'Properties' && (
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Icon name="Building2" size={16} color="var(--color-accent)" />
                              <span className="font-medium text-foreground font-data">{result?.id}</span>
                            </div>
                            <p className="text-sm text-foreground">{result?.address}</p>
                            <p className="text-xs text-muted-foreground">{result?.type} • {result?.bedrooms} bedrooms</p>
                          </div>
                        )}
                        {result?.category === 'Tenancies' && (
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Icon name="User" size={16} color="var(--color-accent)" />
                              <span className="font-medium text-foreground font-data">{result?.id}</span>
                            </div>
                            <p className="text-sm text-foreground">{result?.tenant}</p>
                            <p className="text-xs text-muted-foreground">{result?.property} • {result?.status}</p>
                          </div>
                        )}
                        {result?.category === 'Contractors' && (
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Icon name="Wrench" size={16} color="var(--color-accent)" />
                              <span className="font-medium text-foreground font-data">{result?.id}</span>
                            </div>
                            <p className="text-sm text-foreground">{result?.name}</p>
                            <p className="text-xs text-muted-foreground">{result?.trade} • {result?.phone}</p>
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!searchQuery && (
            <div className="text-center py-8">
              <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
              <p className="text-muted-foreground">Start typing to search across properties, tenants, and contractors</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GlobalSearch;